import useProductCardV2View from '@components/Product/components/ProductCardV2/useProductCardV2View';
import * as R from 'ramda';
import LangEnum from '../../../../../../lang/LangEnum';
import useSharedEntitystore from '../../../../../../stores/SharedEntities/sharedEntities.store';
import { uniqueColorImageUrl } from '../../../../../../utils/image.constants';
import { OrderProductPriceGridV2 } from '../../../../../orders/products/product/types/orderProductPriceV2.types';
import { AvailabilityEnum } from '../../../../../products/ProductTypes';
import { isItemInOrderStatus } from '../../../../../products/products-details-v2/hooks/useCombination.utils';
import { useProductLock } from '../../../../../products/products-details-v2/hooks/useProductLock';
import useSharedEntities from '../../../../../shared-entities/hooks/useSharedEntities';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useAbility } from '../../../../../users/AbilityContext';
import { CatalogSectionKit } from '../../../interfaces/catalogProductList.types';
import { RowCard } from '../../catalogProductsListV2.types';
import { buildProductCardProps } from '../../components/catalogProductRowV2.utils';
import { useCatalogMassActionsContext } from '../../contexts/CatalogMassActions.context';
import { useCatalogListContextParams } from '../../hooks/useCatalogListContextParams';
import { buildCatalogListRowSelection } from '../utils/catalogListRowSelection.utils';
import {
  resolveBlockedForCustomerTooltip,
  resolveKitButtonStatus,
} from './catalogListAddOrderButton.utils';
import useCatalogKitQuantity from './useCatalogKitQuantity';

const KIT_SIZE_NAME = 'KIT';

interface UseCatalogKitListRowParams {
  catalogId: string;
  card: Extract<RowCard, { kind: 'item' }>;
  kit: CatalogSectionKit;
}

const useCatalogKitListRow = ({ catalogId, card, kit }: UseCatalogKitListRowParams) => {
  const { translate } = useTranslation();
  const { orderId } = useCatalogListContextParams();
  const massActions = useCatalogMassActionsContext();
  const { isPublicAccess, disableProductDetails } = useSharedEntities({});
  const openLoginModal = useSharedEntitystore((state) => state.handleChangeIsOpenLoginModal);
  const ability = useAbility();
  const lock = useProductLock();

  const wholesalePrice = kit.kitWholesalePrice ?? 0;
  const availableAmount = kit.kitAvailableAmount ?? null;

  const productCardProps = buildProductCardProps(card, catalogId);
  const view = useProductCardV2View(productCardProps);

  const { quantity, isRemoving, inputRef, onChangeQuantity, onRemove } = useCatalogKitQuantity({
    kitId: kit.kitId,
    orderId,
    payloadQuantity: kit.orderQuantity ?? 0,
    availableAmount,
    isPublicAccess,
  });

  const priceGrid: OrderProductPriceGridV2[] = [
    {
      orderWholeSalePrice: wholesalePrice,
      productSizeActive: true,
      productSizeName: KIT_SIZE_NAME,
      orderItemQuantity: quantity || undefined,
    },
  ];

  const isInOrder = quantity > 0;
  const isBlockedForCustomer = !card.canAddToOrder && !isInOrder;
  const isNotSellable = !kit.kitAvailableForSale && !isInOrder;
  const isOutOfStock = !R.isNil(availableAmount) && availableAmount <= 0 && !isInOrder;
  const isDisabled =
    isPublicAccess ||
    !ability.can('update', 'orders') ||
    isBlockedForCustomer ||
    isNotSellable ||
    isOutOfStock;

  const gridButtonStatus = resolveKitButtonStatus({ quantity, isRemoving });

  const isPublicFunnel = isPublicAccess && !isItemInOrderStatus(gridButtonStatus);
  const isAddButtonDisabled = !isPublicFunnel && isDisabled;

  const onAdd = () => {
    if (isPublicAccess) {
      openLoginModal(true);
      return;
    }

    onChangeQuantity(1);
  };

  const kitColor = {
    id: kit.kitId,
    productColorId: kit.kitId,
    name: translate(LangEnum.CATALOGS_LIST_KIT_COMBINATION),
    imageUrl: uniqueColorImageUrl,
    hexcode: '',
    isImage: true,
  };

  return {
    leftBlock: {
      name: productCardProps.item.name,
      code: productCardProps.item.code,
      entityRelations: productCardProps.item.entityRelations,
      tags: view.tags,
      imageUrl: kit.kitImageUrl,
      colors: { allColors: [kitColor], selectedColor: kitColor },
      onOpenDetails: disableProductDetails ? undefined : view.onCardClick,
    },
    selection: buildCatalogListRowSelection({ massActions, card, productName: kit.kitName }),
    gridItem: {
      skuId: kit.kitId,
      sizeName: KIT_SIZE_NAME,
      available: true,
      sizeActive: true,
      sizeUnique: false,
      sizePosition: 0,
      quantity,
      availableAmount,
    },
    inputRef,
    stock: availableAmount,
    maxValue: lock.getLimit({
      availableAmount,
      availability: AvailabilityEnum.READY_FOR_DELIVERY,
    }),
    price: {
      grid: priceGrid,
      partsQuantity: quantity,
      total: quantity * wholesalePrice,
      isHidden: view.price.shouldDisableAllPrices || view.price.disableWholesalePrice,
      showTotal: !!orderId,
      hidePricesTooltip: disableProductDetails,
    },
    gridButtonStatus,
    isDisabled,
    isAddButtonDisabled,
    tooltipMessage: resolveBlockedForCustomerTooltip({
      isPublic: isPublicAccess,
      isBlockedForCustomer,
    }),
    onChangeQuantity,
    onAdd,
    onRemove,
  };
};

export default useCatalogKitListRow;
